// src/stores/authStore.js
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_CURRENCY } from '../constants/currencies';

const ROLE_HIERARCHY = [
  'CASHIER',
  'KITCHEN_STAFF',
  'WAITER',
  'MANAGER',
  'ADMIN',
  'SYSTEM',
];

const useAuthStore = create(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      user: null,
      memberships: [],
      currentRole: null,
      restaurantId: null,
      restaurantName: null,
      restaurantLogo: null,
      venueId: null,
      venueName: null,
      currency: DEFAULT_CURRENCY,
      primaryColor: null,
      secondaryColor: null,
      venueOrigin: null,
      isAuthenticated: false,

      setAuth: ({ token, refreshToken = null, user, restaurantName, restaurantLogo, currency, primaryColor, secondaryColor }) => {
        const { memberships: rawMemberships, ...userWithoutMemberships } = user ?? {};
        const memberships = rawMemberships ?? [];

        const restaurantMemberships = memberships.filter(
          (m) => m.scopeType === 'RESTAURANT' && m.isActive,
        );
        const single = restaurantMemberships.length === 1 ? restaurantMemberships[0] : null;

        const venueMemberships = memberships.filter(
          (m) => m.scopeType === 'VENUE' && m.isActive,
        );
        const singleVenue = venueMemberships.length === 1 ? venueMemberships[0] : null;

        set({
          token,
          refreshToken,
          user: userWithoutMemberships,
          memberships,
          restaurantId: single?.scopeId ?? null,
          currentRole: single?.role ?? null,
          restaurantName: restaurantName ?? null,
          restaurantLogo: restaurantLogo || null,
          venueId: singleVenue?.scopeId ?? null,
          venueName: null,
          currency: currency || DEFAULT_CURRENCY,
          primaryColor: primaryColor || null,
          secondaryColor: secondaryColor || null,
          isAuthenticated: true,
        });
      },

      setActiveRestaurant: (scopeId) => {
        const membership = get().memberships.find(
          (m) => m.scopeType === 'RESTAURANT' && m.scopeId === scopeId && m.isActive,
        );
        set({ restaurantId: scopeId, currentRole: membership?.role ?? null });
      },

      setActiveVenue: (scopeId) => {
        set({ venueId: scopeId, venueName: null, restaurantId: null, currentRole: null });
      },

      enterRestaurantFromVenue: (restaurantId, venueOrigin = null, restaurantName = null) => {
        const { venueId } = get();
        const venueRole = get().getVenueRole(venueId);
        set({ restaurantId, restaurantName, currentRole: venueRole ?? 'MANAGER', venueOrigin });
      },

      returnToVenue: () => {
        const { venueOrigin, venueId } = get();
        const venueRole = get().getVenueRole(venueId);
        set({ restaurantId: null, restaurantName: null, currentRole: venueRole ?? null, venueOrigin: null });
        return venueOrigin;
      },

      setVenueName: (venueName) => set({ venueName }),

      setToken: (token) => set({ token, isAuthenticated: !!token }),

      setUser: (user) => set({ user }),

      setRestaurant: (restaurantId, restaurantName, currency, restaurantLogo) =>
        set({
          restaurantId,
          restaurantName,
          ...(currency ? { currency } : {}),
          ...(restaurantLogo !== undefined ? { restaurantLogo } : {}),
        }),

      setCurrency: (currency) => set({ currency }),

      setBrandColors: (primaryColor, secondaryColor) =>
        set({
          primaryColor: primaryColor || null,
          secondaryColor: secondaryColor || null,
        }),

      rehydrateContextFromMemberships: () => {
        const state = get();
        if (!state.isAuthenticated) return;
        if (state.user?.accountType === 'SYSTEM') return;
        if (state.restaurantId && state.currentRole) return;

        const firstActiveRestaurant = (state.memberships || []).find(
          (m) => m.scopeType === 'RESTAURANT' && m.isActive,
        );
        if (!firstActiveRestaurant) return;

        set({
          restaurantId: state.restaurantId || firstActiveRestaurant.scopeId,
          currentRole: state.currentRole || firstActiveRestaurant.role || null,
        });
      },

      logout: () =>
        set({
          token: null,
          refreshToken: null,
          user: null,
          memberships: [],
          currentRole: null,
          restaurantId: null,
          restaurantName: null,
          restaurantLogo: null,
          venueId: null,
          venueName: null,
          currency: DEFAULT_CURRENCY,
          primaryColor: null,
          secondaryColor: null,
          venueOrigin: null,
          isAuthenticated: false,
        }),

      signout: async () => {
        try {
          const { default: apiClient } = await import('../api/client');
          await apiClient.post('/auth/signout');
        } catch {
          // API call failure should not block logout
        } finally {
          try {
            const { supabase } = await import('../supabaseClient');
            await supabase.auth.signOut();
          } catch {
          }
          set({
            token: null,
            refreshToken: null,
            user: null,
            memberships: [],
            currentRole: null,
            restaurantId: null,
            restaurantName: null,
            restaurantLogo: null,
            venueId: null,
            venueName: null,
            currency: DEFAULT_CURRENCY,
            primaryColor: null,
            secondaryColor: null,
            venueOrigin: null,
            isAuthenticated: false,
          });
        }
      },

      getRole: () => {
        const { currentRole, user } = get();
        if (user?.accountType === 'SYSTEM') return 'SYSTEM';
        return currentRole;
      },

      hasRole: (role) => {
        const { currentRole, user } = get();
        // SYSTEM users bypass all role checks — they are platform admins
        if (user?.accountType === 'SYSTEM') return true;
        if (!currentRole) return false;
        return (
          ROLE_HIERARCHY.indexOf(currentRole) >= ROLE_HIERARCHY.indexOf(role)
        );
      },

      getVenueMemberships: () =>
        get().memberships.filter((m) => m.scopeType === 'VENUE' && m.isActive),

      hasVenueMembership: () =>
        get().memberships.some((m) => m.scopeType === 'VENUE' && m.isActive),

      getVenueRole: (venueId) =>
        get().memberships.find(
          (m) => m.scopeType === 'VENUE' && m.scopeId === venueId && m.isActive,
        )?.role ?? null,

      isVenueManager: (venueId) =>
        get().getVenueRole(venueId) === 'MANAGER',
    }),
    {
      name: 'dineo-auth',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        memberships: state.memberships,
        currentRole: state.currentRole,
        restaurantId: state.restaurantId,
        restaurantName: state.restaurantName,
        restaurantLogo: state.restaurantLogo,
        venueId: state.venueId,
        venueName: state.venueName,
        currency: state.currency,
        primaryColor: state.primaryColor,
        secondaryColor: state.secondaryColor,
        venueOrigin: state.venueOrigin,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);

export default useAuthStore;
